import { ServiceException } from '@inter-ikea-kompis/exceptions';
import ItemUtility from '../../utilities/ItemUtility.js';
import ServiceFetch from '../../utilities/ServiceFetch.js';
/**
 * This class is used for fetching product data.
 */
export default class DexfWebplannerService {
    /**
     * Constructor.
     *
     * @param options Options.
     * @param options.endpoint Endpoint.
     * @param options.dexfApiKey DEXF API key.
     */
    constructor(options) {
        this.maxItemsPerRequest = 50;
        this.endpoint = options.endpoint;
        this.dexfApiKey = options.dexfApiKey;
    }
    /**
     * Fetches items from DEXF.
     *
     * @param items Array of items.
     * @param fields Fields.
     * @param params Optional params.
     * @returns Promise.
     */
    async getProducts(items, fields, params = {}) {
        if (!fields) {
            throw new ServiceException('You have to specify fields to fetch product information.');
        }
        const sortedFields = fields.slice();
        const sets = ItemUtility.getItemSets(items, this.maxItemsPerRequest);
        const options = {
            credentials: 'same-origin',
            headers: {
                'dexf-api-key': this.dexfApiKey
            }
        };
        sortedFields.sort();
        const promises = sets.map((set) => {
            const itemId = ItemUtility.getItemsString(set);
            const url = `${this.endpoint}?filter.itemId=${itemId}&fields=${sortedFields.join(',')}${this.getOptionalQueryString(params)}`;
            return ServiceFetch.json(url, options);
        });
        const parts = await Promise.all(promises);
        const products = [];
        for (const part of parts) {
            for (const item of part.data) {
                products.push(this.parseProduct(item));
            }
        }
        return products;
    }
    /**
     * Fetches all items assigned to an application from DEXF.
     *
     * @param dexfApplicationId DEXF application ID.
     * @param fields Fields.
     * @param params Optional params.
     * @returns Promise.
     */
    async getApplicationProducts(dexfApplicationId, fields, params = {}) {
        if (!fields) {
            throw new ServiceException('You have to specify fields to fetch product information.');
        }
        const sortedFields = fields.slice();
        sortedFields.sort();
        const url = `${this.endpoint}?filter.appId=${dexfApplicationId}&fields=${sortedFields.join(',')}${this.getOptionalQueryString(params)}`;
        const response = await ServiceFetch.json(url, {
            credentials: 'same-origin',
            headers: {
                'dexf-api-key': this.dexfApiKey
            }
        });
        const products = [];
        for (const item of response.data) {
            products.push(this.parseProduct(item));
        }
        return products;
    }
    /**
     * Get optional query string.
     *
     * @param params Optional params.
     * @returns Query string.
     */
    getOptionalQueryString(params) {
        let qs = '';
        if (params.tags && params.tags.length > 0) {
            qs += `&filter.tags=${params.tags.join(',')}`;
        }
        if (params.valid !== undefined) {
            qs += `&filter.valid=${params.valid}`;
        }
        if (params.customParams) {
            for (const param in params.customParams) {
                qs += `&${param}=${params.customParams[param]}`;
            }
        }
        return qs;
    }
    /**
     * Parses product data.
     *
     * @param data Data.
     * @returns Item string.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    parseProduct(data) {
        return {
            reason: data.reason || [],
            valid: data.valid || false,
            itemId: data.itemId || null,
            content: data.content ? this.parseProductContent(data.content) : null
        };
    }
    /**
     * Parses product content data.
     *
     * @param data Data.
     * @returns Item string.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    parseProductContent(data) {
        return {
            itemType: data.itemType || null,
            itemNoGlobal: data.itemNoGlobal || null,
            itemNoLocal: data.itemNoLocal || null,
            ruItemType: data.ruItemType || null,
            ruItemNo: data.ruItemNo || null,
            breathTakingItem: data.breathTakingItem || false,
            name: data.name || null,
            mainTypeCode: data.mainTypeCode || null,
            mainTypeName: data.mainTypeName || null,
            typeCode: data.typeCode || null,
            typeName: data.typeName || null,
            validDesignText: data.validDesignText || null,
            colourDotCode: data.colourDotCode || null,
            colourDotUrl: data.colourDotUrl || null,
            requiresAssembly: data.requiresAssembly || null,
            appConfig: data.appConfig || null,
            measure: data.measure || null,
            packageMeasure: data.packageMeasure || null,
            priceInformation: data.priceInformation || null,
            image: data.image || null,
            video: data.video || null,
            goodToKnow: data.goodToKnow || null,
            customerBenefit: data.customerBenefit || null,
            customerBenefitSummary: data.customerBenefitSummary || null,
            complianceTechnical: data.complianceTechnical || null,
            complianceLabel: data.complianceLabel || null,
            complianceInformation: data.complianceInformation || null,
            technicalInformation: data.technicalInformation || null,
            peopleAndPlanet: data.peopleAndPlanet || null,
            careInstruction: data.careInstruction || null,
            customerMaterial: data.customerMaterial || null,
            filterAttribute: data.filterAttribute || null,
            validDesignPart: data.validDesignPart || null,
            priceUnit: data.priceUnit || null,
            child: data.child || null,
            fullyLocalized: data.fullyLocalized || null,
            presentationGroupCode: data.presentationGroupCode || null,
            assemblyTime: data.assemblyTime || null,
            genericProduct: data.genericProduct || null,
            measureReference: data.measureReference || null,
            reason: data.reason || null,
            document: data.document || null,
            complementaryItem: data.complementaryItem || null,
            asset: data.asset || null,
            saleEndDateTime: data.saleEndDateTime || null,
            saleEndDateText: data.saleEndDateText || null
        };
    }
}
//# sourceMappingURL=DexfWebplannerService.js.map